// =============================================================================
//  Copyright   : 2020 semantics GmbH
//  Date        : $Date: 2024-11-06 14:53:23 +0100 (Wed, 06 Nov 2024) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 147274 $
//  Purpose     : hooks for fulltext package
// =============================================================================
// ---Third-party imports
import React from 'react'

// ---Framework imports
import log from 'uix/logging'
import {Actions, Sel} from 'uix/registries'
import {
  useSelector,
  useActions,
  useEffect,
  usePersistentShallow,
  useMemo,
  useApi,
  useCallback,
  useState,
  useDebounce,
} from 'uix/hooks'
import {useGeometryCalculation} from 'uix/viewer/hooks'
import {dict, groupBy, mapItems, size, values, strip, filterItems, sum, items, isNil} from 'ldash'
import {canUseDOM} from 'uix/utils'

// ---Local imports
import {HYPHEN} from './constants'
import styles from './styles.scss'

// ---Globals
export {useGeometryCalculation}

export const useOcrConfig = () => {
  let ocr_cfg = useSelector(root => root.config.ocr)
  let loadCfgOcr = useActions('loadCfgOcr')
  useEffect(() => {
    if (!ocr_cfg && ocr_cfg !== 'LOADING') {
      loadCfgOcr()
    }
  }, [])

  return ocr_cfg === 'LOADING' ? null : ocr_cfg
}

// note: update the version number if you add new values here
export const INITIAL_OPTIONS = {
  infoActive: false,
  splitVertical: true, // splitpane
  autoAlignLines: false, // connect movement in word editor with viewer
  // text line rendering
  fontSize: 14,
  wrapLines: true,
  evalCharmodel: false, // render words with confidence
  // viewer related
  showViewer: true,
  // compare view
  compareMode: 'lines', // or 'diff' or 'pz5'
  diffNormalize: false,
  diffNormalizeBow: false,
  diffSideBySide: true,
  // test result table
  col_w: true,
  col_l: false,
  col_p: false,
  col_b: false,
  col_cer: false,
  col_wer: true,
  col_pz5: true,
  // position Aware Words
  pawCrop: true,
  pawOutlines: {words: false, lines: false},
}

export const INITIAL_VIEWER_OPTIONS = {
  geometry: {words: true, lines: false, paragraphs: false, blocks: false},
  showGeometryOrder: false,
  zoomToWidth: true,
  // viewer geometry edit
  selectGeometry: true, // if false zoom to selected rect
  moveGeometry: false,
  moveLeftTop: true,
  moveRightBottom: true,
}

export const usePersistentOcrViewerOptions = () =>
  usePersistentShallow('OcrViewerOptions', INITIAL_VIEWER_OPTIONS, 1)

export const usePersistentOptions = () =>
  usePersistentShallow('FulltextAdminOptions', INITIAL_OPTIONS, 18)

const getLineCoords = raw_words => {
  /* derive line(s) bbox from words of line
   */
  let indexed_words = raw_words.map((w, index) => [index, ...w])
  let line2words = groupBy(indexed_words, w => w[6])
  let lines = dict(
    mapItems(line2words, (index, line) => {
      let words = line.map(w => w[0])
      if (words.length == 0) {
        return null
      }
      let first_w = line[0]
      let last_w = line.slice(-1)[0]
      return [
        index,
        {
          bbox: {
            x1: first_w[1],
            y1: Math.min(first_w[2], last_w[2]),
            x2: last_w[3],
            y2: Math.max(first_w[4], last_w[4]),
          },
          text: `line ${index}`,
        },
      ]
    })
  )
  return lines
}

export const getLines = raw_words => {
  /* return lines with enriched words
   */
  let indexed_words = raw_words.map((w, index) => [index, ...w])
  let line2words = groupBy(indexed_words, w => w[6])
  let lines = values(line2words).map(line =>
    line.map(w => ({index: w[0], text: w[5], bbox: w.slice(1, 5), readonly: w[7]}))
  )
  let avg_char_widths = []
  // resolve dehyphenized words into text_unhyp
  lines = lines.map((l, i) => {
    let next_line = lines[i + 1]
    let prev_line = lines[i - 1]
    return l.map(({text, bbox, ...rest}, j) => {
      let w = bbox[2] - bbox[0]
      if (text.length && w > 0) {
        avg_char_widths.push(w / text.length)
      }
      let normalized = strip(text)

      if (normalized.endsWith(HYPHEN)) {
        if (next_line?.[0]) {
          normalized = normalized.slice(0, -1) + strip(next_line[0].text)
        } else {
          normalized = normalized.slice(0, -1)
        }
      } else if (j == 0 && prev_line && prev_line.at(-1).text.endsWith(HYPHEN)) {
        normalized =
          strip(prev_line.at(-1).text)
            .toLowerCase()
            .slice(0, -1) + normalized
      }
      return {...rest, bbox, text, text_unhyp: normalized}
    })
  })
  let avg_char_width = avg_char_widths.length ? sum(avg_char_widths) / avg_char_widths.length : 0

  return {lines, avg_char_width}
}

export const usePageData = fulltextInfo =>
  useMemo(() => {
    if (!fulltextInfo) {
      return {pages: [], lineCoords: {}, otids: []}
    }
    let pages = fulltextInfo.pages || [fulltextInfo]
    pages = pages.filter(p => p.wordInfo)
    let otids = pages.map(p => p.otid)
    let lineCoords = dict(pages.map(p => [p.otid, getLineCoords(p.wordInfo.words)]))
    pages = pages.map(p => {
      let {lines, avg_char_width} = getLines(p.wordInfo.words)
      return {...p, lines, avg_char_width}
    })

    return {pages, lineCoords, otids}
  }, [fulltextInfo])

let calculate = (wordInfo, annotate = null) => {
  let {width, height} = wordInfo
  return wordInfo.words.map((w, index) => {
    let [x1, y1, x2, y2, text] = w
    // adding "id" here for single click selection, for option: layers: {geometry: {selectable: true}}
    return {bbox: {x1, y1, x2, y2}, text, index, id: index, ...annotate}
  })
}

export const useWordGeometryUpdate = (vid, pages) => {
  /* prepare hidden word geometry for viewer given by vid
   */
  useGeometryCalculation(
    vid,
    () => {
      return dict(
        pages.map(p => {
          // hidden word geometry
          let geom = {words: calculate(p.wordInfo).map(w => ({...w, hidden: true}))}
          return [p.otid, geom]
        })
      )
    },
    [pages]
  )
}

export const useOcrGeometryUpdate = ({
  vid,
  pageData,
  geomRectSelect,
  deletedWords,
  hoverFocus = true,
  showGeometryOrder,
}) => {
  /* prepare full ocr output geometry for viewer given by vid
  - words, lines, paragraphs, blocks
   */
  let {pages, lineCoords} = pageData
  const [opts] = usePersistentOcrViewerOptions()

  useGeometryCalculation(
    vid,
    () => {
      let res = {}
      if (size(opts.geometry) > 0) {
        res = dict(
          pages.map(p => {
            //~ log.x(p)
            // always push word geometry
            let annotate = {hoverFocus}
            let geom = {
              words: calculate(p.wordInfo, annotate),
              lines: [],
              paragraphs: [],
              blocks: [],
            }

            if (!opts.geometry.words) {
              // but stay hidden
              geom.words = geom.words.map(w => ({...w, hidden: true}))
            }
            let selected_geom_words = geomRectSelect?.page2geom[p.otid]?.words
            if (selected_geom_words) {
              geom.words = geom.words.map(w => ({...w, selected: !!selected_geom_words[w.index]}))
            }
            if (deletedWords?.length > 0) {
              let deleted_idx = new Set(
                deletedWords.filter(w => w.otid == p.otid).map(w => w.index)
              )
              geom.words = geom.words.map(w => ({...w, deleted: deleted_idx.has(w.index)}))
            }
            // all engines give us line information
            let index2line = lineCoords[p.otid]
            if (opts.geometry.lines) {
              geom.lines = values(index2line)
            }
            // some engines give us paragraph information
            if (opts.geometry.paragraphs) {
              geom.paragraphs = p.wordInfo.paragraphs.map(({lines}, i) => {
                let x1 = Math.min(...lines.map(l => index2line[l].bbox.x1))
                let y1 = Math.min(...lines.map(l => index2line[l].bbox.y1))
                let x2 = Math.max(...lines.map(l => index2line[l].bbox.x2))
                let y2 = Math.max(...lines.map(l => index2line[l].bbox.y2))
                return {bbox: {x1, y1, x2, y2}, text: `Paragraph ${i}`}
              })
            }
            // most engines give us block information
            if (opts.geometry.blocks) {
              geom.blocks = p.wordInfo.blocks.map(({bbox, type}, i) => ({
                bbox: {x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3]},
                text: `${type} ${i}`,
              }))
            }
            if (showGeometryOrder) {
              geom = dict(
                mapItems(geom, (g_name, g_data) => [
                  g_name,
                  g_data.map((g, i) => ({...g, label: i + 1})),
                ])
              )
            }
            return [p.otid, geom]
          })
        )
      }
      return res
    },
    [pages, opts.geometry, geomRectSelect, deletedWords]
  )
}

export const useCharmodelAspect = ({otid, test_name, enabled, deps, colorizer, master_mode}) => {
  let params = dict(filterItems({otid, test_name, master_mode}, (k, v) => v))
  let result = useApi('getCharModelDetails', params, {condition: enabled, cached: true, deps})

  return useMemo(() => {
    let language = result.data?.language
    let get_conf_100 = text_unhyp => result.data?.word2conf_100[text_unhyp.toLowerCase()]
    let in_vocab = conf_100 => conf_100 === 100
    let getClassname = ({text_unhyp}) => {
      if (result.loading || !result.data) return null

      let conf_100 = get_conf_100(text_unhyp)
      let name = null
      if (!conf_100) {
        name = 'ignore'
      } else if (conf_100 < 40) {
        name = 'worst'
      } else if (conf_100 < 50) {
        name = 'worse'
      } else if (conf_100 < 60) {
        name = 'bad'
      } else if (conf_100 < 100) {
        name = 'ok'
      } else if (in_vocab(conf_100)) {
        // in-vocab
        name = 'invocab'
      } else if (conf_100 > 100) {
        name = 'good'
      }

      if (colorizer == 'red' && !['worst', 'worse', 'bad'].includes(name)) return null
      return styles[name] || null
    }
    let getTitle = ({text_unhyp}) => {
      let conf_100 = get_conf_100(text_unhyp)
      return conf_100 && !in_vocab(conf_100) ? `${text_unhyp}: ${conf_100.toFixed(0)}` : null
    }

    let renderWord = ({text, text_unhyp}) => {
      // given a word from above getLines()
      if (!result.loading && result.data) {
        let conf_100 = get_conf_100(text_unhyp)
        return (
          <span className={getClassname({text_unhyp})} title={getTitle({text_unhyp})}>
            {text}
          </span>
        )
      }
      return text
    }

    return {renderWord, getClassname, getTitle, language}
  }, [result])
}

export const useViewerMovements = ({scrollToMode = 'jump', align = 'top'}) => {
  /* control a position with toRect()
   */
  // scrollToMode: jump, zoom, native, scroll
  let [position, setPosition] = useState(null)
  let toRect = (id, x1, y1, x2, y2) =>
    setPosition({
      id,
      x1: x1 - 20, // pixels orgwidth
      y1: y1 - 5,
      x2: x2 + 20,
      y2,
      align,
      scrollToMode,
    })
  return {position, toRect}
}

export const useViewerMovementsWords = ({pageData, scrollToMode = 'jump', align = 'top'}) => {
  /* control a position with toLine(), toWord(), toRect() for given pageData
   */
  let {position, toRect} = useViewerMovements({scrollToMode, align})

  let toLine = useCallback(
    (id, line_index) => {
      let {x1, y1, x2, y2} = pageData.lineCoords[id][line_index].bbox
      let h = y2 - y1
      toRect(id, x1, y1 - h, x2, y2 + h)
    },
    [pageData.lineCoords]
  )
  let toWord = useCallback(
    (id, word_index, enlarge_to_chars = 20) => {
      let page = pageData.pages.filter(p => p.otid == id)[0]
      let word = page?.wordInfo.words[word_index]
      if (word) {
        let [x1, y1, x2, y2, text] = word
        let h = y2 - y1
        if (!text.length || enlarge_to_chars == 0) {
          toRect(id, x1, y1 - h, x2, y2 + h, 'center')
        } else {
          let N = Math.max(enlarge_to_chars, text.length)
          let w = x2 - x1
          let w_N = page.avg_char_width * N
          let more_w_half = Math.ceil(w_N - w / 2)
          toRect(id, x1 - more_w_half, y1 - h, x2 + more_w_half, y2 + h, 'center')
        }
      }
    },
    [pageData.lineCoords]
  )

  return {position, toLine, toWord, toRect, scrollToMode}
}

export const useTextLineScrolling = (vid, pages) => {
  // geometry selection: from single mouse click select in geometry
  let page2geom = useSelector(root => root.data.viewer.selectedGeometry[vid])

  let [first_selected_page_otid, first_selected_word] = useMemo(() => {
    if (size(page2geom)) {
      let [page_otid, selected_geom] = items(page2geom)[0]
      return [page_otid, selected_geom.words?.[0]]
    }
    return [null, null]
  }, [page2geom])
  let buildLineScrollId = useCallback((otid, line_idx) => `line_${otid}_${line_idx}`)
  useEffect(() => {
    if (!isNil(first_selected_word)) {
      let page = pages.find(p => p.otid == first_selected_page_otid) // p.wordInfo.words
      if (page) {
        let word = page.wordInfo.words[first_selected_word]
        if (canUseDOM && word) {
          let line_idx = word[5]
          document
            .getElementById(buildLineScrollId(first_selected_page_otid, line_idx))
            .scrollIntoView({block: 'center', behavior: 'smooth'})
        }
      } else {
        log.warn('no page %o found', first_selected_page_otid)
      }
    }
  }, [first_selected_page_otid, first_selected_word])
  return {buildLineScrollId}
}

const EMPTY = {}
export const useFocusedGeometry = vid => {
  let focused_geom = useSelector(root => root.data.viewer.focusedGeometry[vid] || EMPTY)
  // bidirectional selection update is pretty costly
  // slow down here
  return useDebounce(focused_geom, 500)[0]
}
